import React, { useRef, useState, useEffect, useCallback, useImperativeHandle, forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown, ArrowUp } from "lucide-react";
import { twMerge } from "tailwind-merge";

// Fallback for browsers without the scrollend event. Smooth scrolls over a long answer take over a second.
const SMOOTH_SCROLL_MAX_MS = 1500;

interface Props {
  children: React.ReactNode;
  className?: string;
  showScrollButtons?: boolean;
  scrollThreshold?: number;
  autoScrollOnNewContent?: boolean;
}

export interface ScrollableChatRef {
  scrollToBottom: () => void;
  /** Scrolls `element` to the top of the visible area and stops following new content until `scrollToBottom`. */
  pinToTop: (element: HTMLElement) => void;
}

export const ScrollableChat = React.memo(
  forwardRef<ScrollableChatRef, Props>(function ScrollableChat(
    { children, className = "", showScrollButtons = true, scrollThreshold = 100, autoScrollOnNewContent = true },
    ref,
  ) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const [isAtTop, setIsAtTop] = useState(true);
    const [userHasScrolledAway, setUserHasScrolledAway] = useState(false);
    const prevChildrenRef = useRef(children);
    const isAutoScrollingRef = useRef(false);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Refs to track actual scroll position immediately (for auto-scroll logic)
    const isAtBottomRef = useRef(true);
    const isAtTopRef = useRef(true);

    // Kept apart from userHasScrolledAway: a pinned position can also be the bottom of the content,
    // and reaching the bottom re-arms following.
    const pinRef = useRef<{ scrollTop: number; resizeObserver: ResizeObserver } | null>(null);
    const cancelSmoothScrollRef = useRef<(() => void) | null>(null);

    const checkIfAtBottom = useCallback(() => {
      const container = scrollContainerRef.current;
      if (!container) return false;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // Add hysteresis: use larger threshold when transitioning to "at bottom"
      const threshold = isAtBottomRef.current ? scrollThreshold + 10 : scrollThreshold;
      return distanceFromBottom <= threshold;
    }, [scrollThreshold]);

    const checkIfAtTop = useCallback(() => {
      const container = scrollContainerRef.current;
      if (!container) return true;

      const { scrollTop } = container;
      // Add hysteresis: use larger threshold when transitioning to "at top"
      const threshold = isAtTopRef.current ? scrollThreshold + 10 : scrollThreshold;
      // The pinned position counts as the top, so the jump to top button does not cover the pinned element.
      const pin = pinRef.current;
      return scrollTop <= threshold || (pin !== null && Math.abs(scrollTop - pin.scrollTop) <= threshold);
    }, [scrollThreshold]);

    const smoothScrollTo = useCallback((top: number, onSettled?: () => void) => {
      const container = scrollContainerRef.current;
      if (!container) return;

      cancelSmoothScrollRef.current?.();

      // No scrollend fires when the position does not change.
      const reachableTop = Math.min(top, container.scrollHeight - container.clientHeight);
      if (Math.abs(reachableTop - container.scrollTop) < 1) {
        onSettled?.();
        return;
      }

      const stopWaiting = () => {
        container.removeEventListener("scrollend", settle);
        clearTimeout(fallbackTimer);
        cancelSmoothScrollRef.current = null;
        isAutoScrollingRef.current = false;
      };
      const settle = () => {
        stopWaiting();
        onSettled?.();
      };
      const fallbackTimer = setTimeout(settle, SMOOTH_SCROLL_MAX_MS);

      isAutoScrollingRef.current = true;
      cancelSmoothScrollRef.current = stopWaiting;
      container.addEventListener("scrollend", settle);
      container.scrollTo({ top, behavior: "smooth" });
    }, []);

    const scrollToBottom = useCallback(() => {
      pinRef.current?.resizeObserver.disconnect();
      pinRef.current = null;
      setUserHasScrolledAway(false);

      const container = scrollContainerRef.current;
      if (!container) return;

      smoothScrollTo(container.scrollHeight, () => {
        // Content can grow during the scroll, and following only resumes from the bottom.
        container.scrollTop = container.scrollHeight;
        isAtBottomRef.current = true;
      });
    }, [smoothScrollTo]);

    const scrollToTop = useCallback(() => {
      setUserHasScrolledAway(true);
      smoothScrollTo(0);
    }, [smoothScrollTo]);

    const pinToTop = useCallback(
      (element: HTMLElement) => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const syncIsAtBottom = () => {
          const atBottom = checkIfAtBottom();
          isAtBottomRef.current = atBottom;
          setIsAtBottom(atBottom);
        };

        const top = element.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;

        // A pinned view does not scroll while content grows, so no scroll event would reveal the jump to latest button.
        const resizeObserver = new ResizeObserver(() => {
          if (!isAutoScrollingRef.current) syncIsAtBottom();
        });
        resizeObserver.observe(element);

        pinRef.current?.resizeObserver.disconnect();
        pinRef.current = { scrollTop: top, resizeObserver };
        smoothScrollTo(top, syncIsAtBottom);
      },
      [checkIfAtBottom, smoothScrollTo],
    );

    useEffect(
      () => () => {
        pinRef.current?.resizeObserver.disconnect();
        cancelSmoothScrollRef.current?.();
      },
      [],
    );

    useImperativeHandle(ref, () => ({ scrollToBottom, pinToTop }), [scrollToBottom, pinToTop]);

    const handleScroll = useCallback(() => {
      const atBottom = checkIfAtBottom();
      const atTop = checkIfAtTop();

      // Update refs immediately for auto-scroll logic (critical for new messages)
      isAtBottomRef.current = atBottom;
      isAtTopRef.current = atTop;

      // Only reset userHasScrolledAway if we're truly at bottom AND not manually scrolling
      if (!isAutoScrollingRef.current) {
        if (atBottom) {
          // Only reset if we're at the very bottom (strict check)
          const container = scrollContainerRef.current;
          if (container) {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
            // Very strict threshold (1px) - must be at absolute bottom
            if (distanceFromBottom <= 1) {
              setUserHasScrolledAway(false);
            }
          }
        } else {
          setUserHasScrolledAway(prev => (!prev ? true : prev));
        }
      }

      // Debounce ONLY button visibility state updates to prevent flickering
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        setIsAtBottom(atBottom);
        setIsAtTop(atTop);
      }, 150);
    }, [checkIfAtBottom, checkIfAtTop]);

    useEffect(() => {
      if (!autoScrollOnNewContent) return;

      const childrenChanged = prevChildrenRef.current !== children;

      // Use ref for immediate check, not debounced state
      // Only auto-scroll if: 1) at bottom, 2) user hasn't scrolled away, 3) not auto-scrolling already
      const isPinned = pinRef.current !== null;
      if (
        childrenChanged &&
        isAtBottomRef.current &&
        !userHasScrolledAway &&
        !isAutoScrollingRef.current &&
        !isPinned
      ) {
        requestAnimationFrame(() => {
          const container = scrollContainerRef.current;
          if (container && !userHasScrolledAway && pinRef.current === null) {
            // Double-check user hasn't scrolled away during requestAnimationFrame
            isAutoScrollingRef.current = true;
            container.scrollTop = container.scrollHeight;
            setTimeout(() => {
              isAutoScrollingRef.current = false;
            }, 50);
          }
        });
      }

      prevChildrenRef.current = children;
    }, [children, autoScrollOnNewContent, userHasScrolledAway]);

    // Detect manual scroll intent (wheel, touch) to prevent auto-scroll fighting
    const handleManualScroll = useCallback(() => {
      cancelSmoothScrollRef.current?.();

      // If user manually scrolls, immediately mark as scrolled away
      if (!isAutoScrollingRef.current && isAtBottomRef.current) {
        setUserHasScrolledAway(true);
      }
    }, []);

    useEffect(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      // Listen for scroll events
      container.addEventListener("scroll", handleScroll, { passive: true });

      // Listen for manual scroll intent (wheel, touch)
      container.addEventListener("wheel", handleManualScroll, { passive: true });
      container.addEventListener("touchstart", handleManualScroll, { passive: true });

      // Initialize on mount
      handleScroll();

      return () => {
        container.removeEventListener("scroll", handleScroll);
        container.removeEventListener("wheel", handleManualScroll);
        container.removeEventListener("touchstart", handleManualScroll);
        // Clean up pending timeout on unmount
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }, [handleScroll, handleManualScroll]);

    return (
      <div className="relative h-full w-full">
        <div ref={scrollContainerRef} className={twMerge(`h-full w-full overflow-y-auto ${className}`)}>
          {children}
        </div>

        {showScrollButtons && (
          <AnimatePresence>
            {!isAtTop && (
              <div className="pointer-events-none absolute inset-x-0 top-4 z-10 flex justify-center">
                <motion.button
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  onClick={scrollToTop}
                  className="pointer-events-auto flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-lg ring-1 ring-gray-200 transition-all hover:bg-gray-50 hover:shadow-xl"
                  aria-label="Scroll to top"
                >
                  <ArrowUp className="h-4 w-4" />
                  Jump to top
                </motion.button>
              </div>
            )}
          </AnimatePresence>
        )}

        {showScrollButtons && (
          <AnimatePresence>
            {!isAtBottom && (
              <div className="pointer-events-none absolute inset-x-0 bottom-4 z-10 flex justify-center">
                <motion.button
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  onClick={scrollToBottom}
                  className="pointer-events-auto flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-lg ring-1 ring-gray-200 transition-all hover:bg-gray-50 hover:shadow-xl"
                  aria-label="Scroll to bottom"
                >
                  <ArrowDown className="h-4 w-4" />
                  Jump to latest
                </motion.button>
              </div>
            )}
          </AnimatePresence>
        )}
      </div>
    );
  }),
);
